/**
 * useCandidates.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * REST hook for the Candidates tab in the React support console.
 *
 * RESPONSIBILITIES
 * ────────────────
 *   • Fetch the list of candidates from /api/candidates (polled).
 *   • Expose actions: promote / ignore / merge.
 *   • Cache per-route grouping (computed once per fetch).
 *   • Track which candidate ids are currently "in flight" (action pending)
 *     so the UI can disable buttons.
 *
 * The hook is self-contained — pass it the API base URL and the internal
 * secret, get back a {records, refresh, promote, ignore, merge, ...}.
 * No SignalR — candidates are batch/REST data, not event-stream data.
 *
 * AUTO-REFRESH
 * ────────────
 * Polls every POLL_INTERVAL_MS while the tab is visible. When the agent
 * isn't looking at the Candidates tab, no polling — keeps the network
 * quiet during long support sessions.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ── Tunables ─────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 8_000;

// ── Wire types ───────────────────────────────────────────────────────────
export type CandidateStatus = 'pending' | 'promoted' | 'ignored';

export interface CandidateRecord {
  candidateId:       string;
  status:            CandidateStatus;
  allocatedGuid:     string | null;
  label:             string;
  tag:               string;
  cssClass:          string;
  route:             string;
  suggestedParentId: string | null;
  ancestorChain:     string[];
  firstSeen:         string;
  lastSeen:          string;
  occurrences:       number;
  notes:             string | null;
  mergedInto:        string | null;
}

export interface PromoteResponse {
  candidateId: string;
  guid:        string;
  tsSnippet:   string;
}

interface UseCandidatesOptions {
  apiBaseUrl:      string;
  internalSecret:  string;
  enabled:         boolean;   // turn polling on/off based on tab visibility
}

interface UseCandidatesResult {
  records:          CandidateRecord[];
  loading:          boolean;
  error:            string | null;
  lastFetchedAt:    Date | null;
  inFlight:         Set<string>;
  refresh:          () => Promise<void>;
  promote:          (id: string, opts?: { label?: string; parentId?: string; notes?: string }) => Promise<PromoteResponse | null>;
  ignore:           (id: string, notes?: string) => Promise<void>;
  merge:            (canonicalId: string, mergeIds: string[]) => Promise<PromoteResponse | null>;
  counts:           { total: number; pending: number; promoted: number; ignored: number };
  byRoute:          Record<string, CandidateRecord[]>;
}

export function useCandidates(opts: UseCandidatesOptions): UseCandidatesResult {
  const { apiBaseUrl, internalSecret, enabled } = opts;

  const [records,       setRecords]       = useState<CandidateRecord[]>([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const [inFlight,      setInFlight]      = useState<Set<string>>(new Set());

  // Use a ref for the polling interval so we can cancel it cleanly.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  // Headers shared by every fetch.
  const headers = useMemo(() => ({
    'Content-Type':      'application/json',
    'X-Internal-Secret': internalSecret,
  }), [internalSecret]);

  // ── Fetch all records ────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!apiBaseUrl) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/candidates`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json() as CandidateRecord[];
      setRecords(Array.isArray(data) ? data : []);
      setError(null);
      setLastFetchedAt(new Date());
    } catch (e: any) {
      setError(e?.message ?? 'Failed to fetch candidates');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, headers]);

  // ── Auto-poll when enabled ────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !apiBaseUrl) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    // Immediate fetch on enable.
    refresh();
    pollRef.current = setInterval(() => {
      if (enabledRef.current) refresh();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [enabled, apiBaseUrl, refresh]);

  // ── Helpers to mark in-flight (action pending) ───────────────────────
  const startAction = (id: string) =>
    setInFlight(prev => { const next = new Set(prev); next.add(id); return next; });
  const endAction = (id: string) =>
    setInFlight(prev => { const next = new Set(prev); next.delete(id); return next; });

  // ── Promote ──────────────────────────────────────────────────────────
  const promote = useCallback(async (
    id: string,
    options?: { label?: string; parentId?: string; notes?: string },
  ): Promise<PromoteResponse | null> => {
    if (!apiBaseUrl) return null;
    startAction(id);
    try {
      const res = await fetch(`${apiBaseUrl}/api/candidates/promote`, {
        method:  'POST',
        headers,
        body:    JSON.stringify({ candidateId: id, ...options }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text}`);
      }
      const out = await res.json() as PromoteResponse;
      // Optimistically update locally so the UI updates instantly.
      setRecords(prev => prev.map(r => r.candidateId === id
        ? { ...r, status: 'promoted' as const, allocatedGuid: out.guid,
                  label: options?.label ?? r.label }
        : r));
      return out;
    } catch (e: any) {
      setError(e?.message ?? 'Promote failed');
      return null;
    } finally {
      endAction(id);
    }
  }, [apiBaseUrl, headers]);

  // ── Ignore ───────────────────────────────────────────────────────────
  const ignore = useCallback(async (id: string, notes?: string): Promise<void> => {
    if (!apiBaseUrl) return;
    startAction(id);
    try {
      const res = await fetch(`${apiBaseUrl}/api/candidates/ignore`, {
        method:  'POST',
        headers,
        body:    JSON.stringify({ candidateId: id, notes }),
      });
      if (!res.ok && res.status !== 204) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text}`);
      }
      setRecords(prev => prev.map(r => r.candidateId === id
        ? { ...r, status: 'ignored' as const }
        : r));
    } catch (e: any) {
      setError(e?.message ?? 'Ignore failed');
    } finally {
      endAction(id);
    }
  }, [apiBaseUrl, headers]);

  // ── Merge (canonical wins; merge ids share its GUID) ─────────────────
  const merge = useCallback(async (
    canonicalId: string,
    mergeIds: string[],
  ): Promise<PromoteResponse | null> => {
    if (!apiBaseUrl) return null;
    const allIds = [canonicalId, ...mergeIds];
    allIds.forEach(startAction);
    try {
      const res = await fetch(`${apiBaseUrl}/api/candidates/merge`, {
        method:  'POST',
        headers,
        body:    JSON.stringify({ canonicalId, mergeIds }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text}`);
      }
      const out = await res.json() as PromoteResponse;
      setRecords(prev => prev.map(r => allIds.includes(r.candidateId)
        ? { ...r, status: 'promoted' as const, allocatedGuid: out.guid,
                  mergedInto: r.candidateId === canonicalId ? null : canonicalId }
        : r));
      return out;
    } catch (e: any) {
      setError(e?.message ?? 'Merge failed');
      return null;
    } finally {
      allIds.forEach(endAction);
    }
  }, [apiBaseUrl, headers]);

  // ── Derived data ─────────────────────────────────────────────────────
  const counts = useMemo(() => ({
    total:    records.length,
    pending:  records.filter(r => r.status === 'pending').length,
    promoted: records.filter(r => r.status === 'promoted').length,
    ignored:  records.filter(r => r.status === 'ignored').length,
  }), [records]);

  const byRoute = useMemo(() => {
    const grouped: Record<string, CandidateRecord[]> = {};
    for (const r of records) {
      const key = r.route || '(unknown route)';
      (grouped[key] ||= []).push(r);
    }
    // Sort each group by lastSeen desc (newest first within a route).
    for (const key of Object.keys(grouped)) {
      grouped[key].sort((a, b) => b.lastSeen.localeCompare(a.lastSeen));
    }
    return grouped;
  }, [records]);

  return {
    records, loading, error, lastFetchedAt, inFlight,
    refresh, promote, ignore, merge,
    counts, byRoute,
  };
}
